<template>
    <div class="input-container mt-3">
        <div class="row">
            <div class="col-md-2">
                <InputField label="Select Database" type="select" v-model="database" :editable="true" :options="databaseOptions" @update:model-value="handleDatabaseChange" />
            </div>
            <div class="col-md-2" v-if="database">
                <div v-if="tablesLoading" class="text-center">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else>
                    <InputField label="Select Table" type="select" v-model="table" :editable="true" :options="tableOptions" :disabled="!database" />
                </div>
            </div>
            <div class="col-md-2 align-self-center">
                <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="searchModeSwitch" v-model="useEntityIdSearch">
                    <label class="form-check-label" for="searchModeSwitch">
                        Search by Entity ID
                    </label>
                </div>
            </div>
            <div class="col-md-2">
                <InputField 
                    v-if="useEntityIdSearch" 
                    label="Entity ID" 
                    type="text" 
                    v-model="entityId" 
                    :editable="true" 
                    placeholder="Enter entity ID" 
                />
                <InputField 
                    v-else 
                    label="Limit" 
                    type="number" 
                    v-model="limit" 
                    :editable="true" 
                />
            </div>
            <div class="col-md-2">
                <InputField label="Depth" type="number" v-model="depth" :editable="true" />
            </div>
            <div class="col-md-2 align-self-center">
                <button class="btn btn-primary w-100" @click="updateGraph" :disabled="loading || !canUpdate">
                    <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <div v-else>
                        <i class="bi bi-search me-2"></i>
                        Update
                    </div>
                </button>
            </div>
        </div>
    </div>

    <D3ForceGraph :data="graphData" />
</template>

<script>
import axios from "axios"
import { mapActions, mapGetters } from 'vuex'
import InputField from "./InputField.vue"
import D3ForceGraph from './D3ForceGraph.vue'

export default {
    components: { InputField, D3ForceGraph },
    data() {
        return {
            database: "",
            table: "",
            depth: 2,
            limit: 10,
            entityId: "",
            useEntityIdSearch: false,
            loading: false,
            tablesLoading: false,
            databaseOptions: [],
            tableOptions: [],
            graphData: null
        }
    },

    computed: {
        ...mapGetters('idr', ['getNetworkData']),
        networkData() {
            return this.getNetworkData;
        },
        canUpdate() {
            if (!this.database || !this.table) return false;
            if (this.useEntityIdSearch) return this.entityId && this.entityId.trim() !== '';
            return this.limit > 0 && this.depth > 0;
        },
        graphQuery() {
            let query = `WITH RECURSIVE hierarchy(entity_type,entity_domain,entity_id,entity_neighbor_type,entity_neighbor_domain,entity_neighbors,depth) AS (
                SELECT
                    t.entity_type,
                    t.entity_domain,
                    t.entity_id,
                    t.entity_neighbor_type,
                    t.entity_neighbor_domain,
                    t.entity_neighbors,
                    0 AS depth
                FROM "${this.database}"."${this.table}" AS t
                `
            
            if (this.useEntityIdSearch) {
                query += `
                    WHERE t.entity_id = '${this.entityId}'
                    `
            } else {
                query += `
                    WHERE t.entity_id IN (SELECT entity_id FROM "${this.database}"."${this.table}" ORDER BY RANDOM() LIMIT ${this.limit})
                    `
            }

            query += `
                UNION ALL

                SELECT
                    child.entity_type,
                    child.entity_domain,
                    child.entity_id,
                    child.entity_neighbor_type,
                    child.entity_neighbor_domain,
                    child.entity_neighbors,
                    parent.depth + 1 AS depth
                FROM hierarchy AS parent
                    JOIN LATERAL (
                        SELECT r.id
                        FROM UNNEST(parent.entity_neighbors) AS t(r)
                    ) AS rel
                    ON TRUE
                    JOIN "${this.database}"."${this.table}" AS child
                    ON child.entity_id = rel.id
                        AND child.entity_type   = parent.entity_type
                        AND child.entity_domain = parent.entity_domain
                WHERE parent.depth < ${this.depth}
            )
            SELECT DISTINCT
                entity_type, 
                entity_domain, 
                entity_id,
                entity_neighbor_type,
                entity_neighbor_domain,
                n.id AS neighbor_entity_id
            FROM hierarchy, UNNEST(entity_neighbors) AS t(n) 
            ORDER BY entity_type, entity_domain, entity_id, entity_neighbor_type, entity_neighbor_domain;
            `
            return query
        }
    },

    methods: {
        ...mapActions({
            fetchAndConvertNetworkData: "idr/fetchAndConvertNetworkData",
        }),

        async getDatabases() {
            try {
                const response = await axios.get(
                    `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/dbs`,
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                        },
                    }
                );
                
                if (response.data && Array.isArray(response.data)) {
                    this.databaseOptions = response.data.map(db => ({
                        label: db,
                        value: db
                    }));
                }
            } catch (error) {
                console.error('Error fetching databases:', error);
            }
        },

        async handleDatabaseChange(newValue) {
            this.table = "";
            this.tableOptions = [];
            this.tablesLoading = true;

            if (!newValue) return;
            
            try {
                const response = await axios.get(
                    `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/dbs/${newValue}/tables`,
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                        },
                    }
                );
                
                if (response.data) {
                    let tables = response.data.tables
                    if (response.data.views) {
                        tables = tables.concat(response.data.views)
                    }
                    this.tableOptions = tables.map(table => ({
                        label: table,
                        value: table
                    }));
                }
            } catch (error) {
                console.error('Error fetching tables:', error);
            } finally {
                this.tablesLoading = false;
            }
        },

        async updateGraph() {
            this.loading = true;
            try {
                const networkData = await this.fetchAndConvertOlapData();
                this.$store.commit('idr/setNetworkData', networkData);

                if (!networkData || !networkData.nodes || !networkData.edges) {
                    console.error('Invalid network data:', networkData);
                    return;
                }

                const graph = { nodes: [], edges: [] };
                networkData.nodes.forEach(node => {
                    graph.nodes.push({ id: node.id, type: node.type, domain: node.domain, key: node.key });
                });
                networkData.edges.forEach(edge => {
                    graph.edges.push({ source: edge.from, target: edge.to });
                });
                this.graphData = graph;
            } catch (error) {
                console.error('Search failed:', error);
            } finally {
                this.loading = false;
            }
        },

        async fetchAndConvertOlapData() {
            try {
                const response = await axios.post(
                    `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/query_executions`,
                    { query: this.graphQuery },
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                const queryExecutionId = response.data?.query_execution_id;
                if (!queryExecutionId) throw new Error("No query_execution_id in response");

                await this.sleep(1000);

                const maxRetries = 60;
                const delay = 1000;

                for (let i = 0; i < maxRetries; i++) {
                    const statusResponse = await axios.get(
                        `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/query_status/${queryExecutionId}`,
                        { headers: { Authorization: `Bearer ${this.$store.state.accessToken}` } }
                    );

                    const statusData = statusResponse.data;
                    if (statusData.State === 'FAILED') throw new Error(statusData.StateChangeReason || 'Query failed');

                    if (statusData.State && statusData.State !== 'QUEUED' && statusData.State !== 'RUNNING') {
                        const resultsResponse = await axios.get(
                            `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/query_executions/${queryExecutionId}`,
                            { headers: { Authorization: `Bearer ${this.$store.state.accessToken}` } }
                        );
                        return this.processQueryResults(resultsResponse.data);
                    }
                    await this.sleep(delay);
                }
                throw new Error("Query timed out after maximum retries");
            } catch (error) {
                console.error('Error in fetchAndConvertOlapData:', error);
                throw error;
            }
        },

        processQueryResults(queryData) {
            const nodes = [];
            const edges = [];

            if (queryData.data && queryData.column_info) {
                queryData.data.forEach(row => {
                    const rowObj = {};
                    for (let i = 0; i < queryData.column_info.length; i++) {
                        rowObj[queryData.column_info[i].name] = row[i];
                    }

                    if (rowObj.entity_id && rowObj.entity_type && rowObj.entity_domain) {
                        const nodeId = `${rowObj.entity_type}~${rowObj.entity_domain}~${rowObj.entity_id}`;
                        
                        if (!nodes.find(n => n.id === nodeId)) {
                            nodes.push({ id: nodeId, type: rowObj.entity_type, domain: rowObj.entity_domain, key: rowObj.entity_id });
                        }

                        if (rowObj.neighbor_entity_id && rowObj.entity_neighbor_type && rowObj.entity_neighbor_domain) {
                            const neighborId = `${rowObj.entity_neighbor_type}~${rowObj.entity_neighbor_domain}~${rowObj.neighbor_entity_id}`;
                            
                            if (!nodes.find(n => n.id === neighborId)) {
                                nodes.push({ id: neighborId, type: rowObj.entity_neighbor_type, domain: rowObj.entity_neighbor_domain, key: rowObj.neighbor_entity_id });
                            }

                            if (!edges.find(e => e.from === nodeId && e.to === neighborId)) {
                                edges.push({ from: nodeId, to: neighborId, fromType: rowObj.entity_type, toType: rowObj.entity_neighbor_type });
                            }
                        }
                    }
                });
            }
            return { nodes, edges };
        },

        sleep(time) {
            return new Promise(resolve => setTimeout(resolve, time));
        }
    },

    mounted() {
        this.getDatabases()
    }
}
</script>
